import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Text } from '@workwhile/ui'
import { useRequirementDetailContextValue } from '../../context/RequirementDetailProvider'
import { format } from 'date-fns'
import { ActionsCell } from '../ActionsCell'

export type Questions = ReturnType<
  typeof useRequirementDetailContextValue
>['state']['questions']

const getQuestionTextColor = (question: Questions[number]) =>
  question.deletedAt !== null ? 'lightText' : undefined

const getDeletedMainColumnStyle = (question: Questions[number]) =>
  question.deletedAt !== null
    ? { textDecoration: 'line-through' as const }
    : undefined

const IdColumn: ColumnDef<Questions[number]> = {
  header: 'ID',
  accessorKey: 'id',
  minSize: 100,
  maxSize: 100,
  cell: ({ row }) => {
    const question = row.original
    return <Text color={getQuestionTextColor(question)}>{question.id}</Text>
  },
}

const QuestionColumn: ColumnDef<Questions[number]> = {
  header: 'Question',
  accessorKey: 'question',
  minSize: 200,
  cell: ({ row }) => {
    const question = row.original

    return (
      <Text
        color={getQuestionTextColor(question)}
        style={getDeletedMainColumnStyle(question)}
      >
        {question.question}
      </Text>
    )
  },
}

const IdealAnswerColumn: ColumnDef<Questions[number]> = {
  header: 'Ideal Answer',
  accessorKey: 'idealAnswer',
  minSize: 200,
  cell: ({ row }) => {
    const question = row.original

    return (
      <Text
        color={getQuestionTextColor(question)}
        style={getDeletedMainColumnStyle(question)}
      >
        {question.idealAnswer}
      </Text>
    )
  },
}

const EvaluationCriteriaColumn: ColumnDef<Questions[number]> = {
  header: 'Evaluation Criteria',
  accessorKey: 'evaluationCriteria',
  minSize: 200,
  cell: ({ row }) => {
    const question = row.original

    return (
      <Text
        color={getQuestionTextColor(question)}
        style={getDeletedMainColumnStyle(question)}
      >
        {question.evaluationCriteria}
      </Text>
    )
  },
}

const CreatedAtColumn: ColumnDef<Questions[number]> = {
  header: 'Created At',
  accessorKey: 'createdAt',
  minSize: 100,
  maxSize: 100,
  cell: ({ row }) => {
    const question = row.original
    return (
      <Text color={getQuestionTextColor(question)}>
        {format(question.createdAt, 'MM/dd/yyyy')}
      </Text>
    )
  },
}

const UpdatedAtColumn: ColumnDef<Questions[number]> = {
  header: 'Updated At',
  accessorKey: 'updatedAt',
  minSize: 100,
  maxSize: 100,
  cell: ({ row }) => {
    const question = row.original
    return (
      <Text color={getQuestionTextColor(question)}>
        {format(question.updatedAt, 'MM/dd/yyyy')}
      </Text>
    )
  },
}

const DeletedAtColumn: ColumnDef<Questions[number]> = {
  header: 'Deleted At',
  accessorKey: 'deletedAt',
  minSize: 100,
  maxSize: 100,
  cell: ({ row }) => {
    const question = row.original
    return (
      <Text color={getQuestionTextColor(question)}>
        {question.deletedAt !== null
          ? format(question.deletedAt, 'MM/dd/yyyy')
          : '-'}
      </Text>
    )
  },
}

export const ActionsColumn: ColumnDef<Questions[number]> = {
  header: 'Actions',
  size: 70,
  cell: ({ row }) => <ActionsCell question={row.original} />,
}

export const selectableColumns = [
  {
    id: 'id',
    label: 'ID',
    columnDef: IdColumn,
  },
  {
    id: 'question',
    label: 'Question',
    columnDef: QuestionColumn,
  },
  {
    id: 'idealAnswer',
    label: 'Ideal Answer',
    columnDef: IdealAnswerColumn,
  },
  {
    id: 'evaluationCriteria',
    label: 'Evaluation Criteria',
    columnDef: EvaluationCriteriaColumn,
  },
  {
    id: 'createdAt',
    label: 'Created At',
    columnDef: CreatedAtColumn,
  },
  {
    id: 'updatedAt',
    label: 'Updated At',
    columnDef: UpdatedAtColumn,
  },
  {
    id: 'deletedAt',
    label: 'Deleted At',
    columnDef: DeletedAtColumn,
  },
] as const
